import React from "react";
import { ArrowDownToLine, Plus } from "lucide-react";
import { StationsTable } from "../../components/StationTable";
import { Pagination } from "../../components/Pagination";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  deleteStation,
  getAllStations,
  getStationQrCode,
} from "../../api/station";
import { useCopyContext } from "../../context/CopyContext";
import { StationFormData } from "../../schemas/station";
import jsPDF from "jspdf";
import "jspdf-autotable";

const ITEMS_PER_PAGE = 10;
const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setCopiedStation } = useCopyContext();

  const { data, isLoading } = useQuery({
    queryKey: ["stations"],
    queryFn: () => getAllStations(),
  });

  const handleEdit = (id: number) => {
    navigate(`/admin/dashboard/station/${id}`);
  };

  const handleDelete = async (id: number) => {
    const response = await deleteStation(id);
    if (response.success) {
      queryClient.refetchQueries({
        queryKey: ["stations"],
      });
    }
  };

  const handleGetAllQrCodes = async () => {
    if (!data || data.length === 0) return;

    try {
      //@ts-ignore
      const qrCodePromises = data.map((station) =>
        getStationQrCode(station.id)
      );
      const qrCodeResults = await Promise.all(qrCodePromises);

      const validQrCodes = qrCodeResults.filter((result) => result !== null);
      const pdf = new jsPDF();

      validQrCodes.forEach((qrCode, index) => {
        if (index > 0) {
          pdf.addPage();
        }

        const qrCodeImg = qrCode.qrCode;
        pdf.addImage(qrCodeImg, "PNG", 60, 50, 80, 80);
        pdf.setFontSize(12);
        pdf.text(`Station Name: ${qrCode.name}`, 105, 140, { align: "center" });
        pdf.text(`Connector ID: ${qrCode.connectorId}`, 105, 150, {
          align: "center",
        });
      });
      pdf.save("station-qrcodes.pdf");
    } catch (error) {
      console.error("Error generating QR codes PDF:", error);
    }
  };

  const handleAddNew = () => {
    navigate("/admin/dashboard/station");
  };

  const onCopy = (station: StationFormData) => {
    setCopiedStation(station);
  };

  return (
    <main className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col sm:flex-row justify-end items-start sm:items-center gap-4 mb-6">
        <div className="w-full sm:w-auto min-w-[300px]"></div>
        <button
          onClick={handleGetAllQrCodes}
          className="w-full sm:w-auto flex items-center justify-center gap-2 bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors"
        >
          <ArrowDownToLine className="w-5 h-5" />
          Get all QR Codes
        </button>
        <button
          onClick={handleAddNew}
          className="w-full sm:w-auto flex items-center justify-center gap-2 bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Add New Station
        </button>
      </div>

      <div className="space-y-4">
        <StationsTable
          stations={isLoading ? [] : data}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onCopy={onCopy}
        />

        <Pagination
          totalCount={isLoading ? 0 : data.length}
          pageSize={ITEMS_PER_PAGE}
          currentPage={1}
          onPageChange={() => {}}
        />
      </div>
    </main>
  );
};

export default Dashboard;
